import type { SceneRig } from "../render/scene";
import { QUALITY_TIERS } from "../render/quality";
import { Settings } from "./settings";
import type { Object3D } from "three";

/** What the overlay reads from a running level: per-phase JS time, and its layers to toggle. */
export interface LevelProbe {
  readonly phaseMs: { sim: number; ui: number; views: number; render: number; frames: number };
  debugLayers(): Record<string, Object3D[]>;
}

/**
 * Debug performance overlay: frame rate, frame-time spikes, render cost, GPU load and memory, with live switches for
 * the usual suspects (resolution, shadows, bloom) so a lagging phone can be diagnosed on the phone.
 *
 * FPS and the worst frame come from this overlay's own requestAnimationFrame, i.e. what the display actually got.
 * "render" is JavaScript time spent in SceneRig.render (submitting draw calls), not GPU time; when FPS is low while
 * render and the frame's script time are small, the GPU is the bottleneck, and the switches show which part of it.
 * JS heap is only reported by Chromium; Safari exposes no memory API, so on iPhone the texture and geometry counts
 * are the memory signal.
 */
export class PerfOverlay {
  private readonly root: HTMLDivElement;
  private readonly text: HTMLPreElement;
  private raf = 0;
  private last = 0;
  private frames = 0;
  private worst = 0;
  private windowStart = 0;
  private renderMs = 0;
  private renderedFrames = 0;
  private lastRigFrames = 0;
  private longTasks = 0;
  private observer: PerformanceObserver | null = null;
  /** Button labels follow the governor's own changes too, not just taps. */
  private readonly repaints: (() => void)[] = [];

  constructor(parent: HTMLElement, private readonly rig: SceneRig, private readonly level: () => LevelProbe | null = () => null) {
    this.root = document.createElement("div");
    this.root.id = "perf";
    this.text = document.createElement("pre");
    const bar = document.createElement("div");
    bar.className = "switches";
    const button = (label: () => string, act: () => void) => {
      const b = document.createElement("button");
      const paint = () => { b.textContent = label(); };
      this.repaints.push(paint);
      b.onclick = (e) => { e.stopPropagation(); act(); paint(); };
      paint();
      bar.append(b);
    };
    // Quality tier first: tapping cycles it. The tier is fixed unless the governor runs (`?autoq`), when "auto" marks
    // it and a tap pins your choice. The switches after it override single settings, for finding which one a device
    // cannot afford.
    button(() => `q ${rig.quality.current.name}${rig.quality.locked ? "" : " auto"}`, () => rig.quality.set((rig.quality.index + 1) % QUALITY_TIERS.length));
    const ratios = [1, 1.5, 2, 3].filter((r) => r <= Math.max(1, window.devicePixelRatio || 1));
    if (!ratios.includes(rig.pixelRatio)) ratios.push(rig.pixelRatio);
    button(() => `res ×${rig.pixelRatio}`, () => { const i = ratios.indexOf(rig.pixelRatio); rig.setPixelRatio(ratios[(i + 1) % ratios.length]); });
    button(() => `shadows ${rig.shadowsOn ? "on" : "off"}`, () => { rig.quality.locked = true; rig.setShadows(!rig.shadowsOn); });
    button(() => `bloom ${rig.bloomOn ? "on" : "off"}`, () => rig.setBloom(!rig.bloomOn));
    // Layer switches: hide one part of the scene and watch the fps. Whichever brings it back is what to cut.
    const layers = document.createElement("div");
    layers.className = "switches layers";
    for (const name of ["ground", "scatter", "props", "towers", "units", "fx"]) {
      const b = document.createElement("button");
      b.textContent = name;
      b.onclick = (e) => {
        e.stopPropagation();
        const objs = this.level()?.debugLayers()[name] ?? [];
        const show = b.classList.toggle("off") === false;
        for (const o of objs) o.visible = show;
      };
      layers.append(b);
    }
    // Tap the readout to fold the panel down to an fps pill (tap the pill to open); the × hides it — it comes back
    // from Settings → Performance overlay. The fold is remembered across launches. Only the readout, the pill and the
    // buttons take touches (hud.css): the panel's padding and gaps let taps through to the map.
    this.text.onclick = (e) => { e.stopPropagation(); this.setFolded(!this.folded); };
    this.pill = document.createElement("button");
    this.pill.className = "pill";
    this.pill.onclick = (e) => { e.stopPropagation(); this.setFolded(false); };
    const close = document.createElement("button");
    close.className = "close";
    close.textContent = "×";
    close.setAttribute("aria-label", "Hide performance overlay");
    close.onclick = (e) => { e.stopPropagation(); Settings.set({ perfHud: false }); };
    this.root.append(close, this.pill, this.text, bar, layers);
    this.setFolded(PerfOverlay.loadFolded());
    // First child of the UI layer: every screen, sheet, dialog and HUD panel is appended after it and so paints over
    // it (they share one stacking context), and the opaque loading and title screens hide it. It must never cover a
    // control, only the map.
    parent.prepend(this.root);
    try {
      this.observer = new PerformanceObserver((list) => { this.longTasks += list.getEntries().length; });
      this.observer.observe({ entryTypes: ["longtask"] });
    } catch { this.observer = null; } // Safari has no long-task timing
    this.windowStart = this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  /** Below this frame rate the readout turns red: well short of 60, where a phone game visibly stutters. */
  static readonly SlowFps = 45;
  private pill!: HTMLButtonElement;
  private folded = false;
  private static readonly FoldKey = "highmarch.perf.folded";
  private setFolded(on: boolean): void {
    this.folded = on;
    this.root.classList.toggle("folded", on);
    try { localStorage.setItem(PerfOverlay.FoldKey, on ? "1" : "0"); } catch { /* private mode */ }
  }
  private static loadFolded(): boolean { try { return localStorage.getItem(PerfOverlay.FoldKey) === "1"; } catch { return false; } }

  private readonly tick = (now: number): void => {
    const dt = now - this.last;
    this.last = now;
    this.frames++;
    this.worst = Math.max(this.worst, dt);
    const f = this.rig.frameStats;
    if (f.frames !== this.lastRigFrames) { this.lastRigFrames = f.frames; this.renderMs += f.cpuMs; this.renderedFrames++; }
    if (now - this.windowStart >= 1000) this.paint(now);
    this.raf = requestAnimationFrame(this.tick);
  };

  private paint(now: number): void {
    const span = now - this.windowStart;
    const fps = (this.frames * 1000) / span;
    const f = this.rig.frameStats;
    const info = this.rig.renderer.info;
    const canvas = this.rig.renderer.domElement;
    const heap = (performance as unknown as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    const shaderErrors = this.rig.shaderErrors;
    const probe = this.level();
    let phases = "";
    if (probe && probe.phaseMs.frames > 0) {
      const p = probe.phaseMs, n = p.frames;
      phases = `sim ${(p.sim / n).toFixed(1)}  ui ${(p.ui / n).toFixed(1)}  views ${(p.views / n).toFixed(1)}  draw ${(p.render / n).toFixed(1)} ms`;
      p.sim = p.ui = p.views = p.render = 0; p.frames = 0;
    }
    const lines = [
      `fps ${fps.toFixed(0).padStart(3)}   worst ${this.worst.toFixed(0)} ms`,
      `render ${(this.renderedFrames ? this.renderMs / this.renderedFrames : 0).toFixed(1)} ms cpu${this.observer ? `   long tasks ${this.longTasks}` : ""}`,
      ...(phases ? [phases] : []),
      `draws ${f.calls}   tris ${(f.triangles / 1000).toFixed(0)}k`,
      `geo ${info.memory.geometries}   tex ${info.memory.textures}   prog ${info.programs?.length ?? 0}`,
      `canvas ${canvas.width}×${canvas.height} @${this.rig.pixelRatio}   q ${this.rig.quality.current.name}${this.rig.quality.locked ? "" : " (auto)"}`,
      ...(shaderErrors.length ? [`SHADER FAIL ${shaderErrors.join(" | ")}`] : []),
      heap ? `heap ${(heap.usedJSHeapSize / 2 ** 20).toFixed(0)} / ${(heap.jsHeapSizeLimit / 2 ** 20).toFixed(0)} MB` : `heap n/a (Safari)`,
    ];
    this.text.textContent = lines.join("\n");
    for (const r of this.repaints) r();
    this.root.classList.toggle("slow", fps < PerfOverlay.SlowFps);
    this.pill.textContent = `${fps.toFixed(0)} fps`;
    this.windowStart = now; this.frames = 0; this.worst = 0; this.renderMs = 0; this.renderedFrames = 0; this.longTasks = 0;
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    this.root.remove();
  }
}
